import React, { useState, useId } from 'react';
import styled from 'styled-components';
import { useAppContext } from 'src/components/app-context';
import {
  TitleSmallText,
  BodySmallText,
  FontColor,
  FontEmphasis,
} from '@prisjakt/ui/text/variants';
import { Popover, Alignment } from '@prisjakt/ui/popover';
import { IconOutgoingLink } from '@prisjakt/ui/icon/outgoing-link';

import { zindex } from 'src/ui/zindex';

import { OfferLink, OfferStoreIconWrapper } from './styles';
import { BaseProps } from '../../types';

type Props = Pick<
  BaseProps,
  'name' | 'link' | 'highlightedName' | 'onClick' | 'store'
>;

export const CompactListCardOfferContent: React.FC<Props> = props => {
  const { name = '', highlightedName, link, onClick, store } = props;
  const { t } = useAppContext();
  const [showPopover, setShowPopover] = useState(false);
  const uid = useId();
  const disabled = !onClick || !link;

  const handleClick = () => {
    if (disabled) {
      setShowPopover(true);
    }

    if (onClick) {
      onClick();
    }
  };

  const getContainerProps = () => {
    const containerProps: any = {
      target: '_blank',
      rel: 'nofollow',
    };

    if (!disabled) {
      containerProps.href = link;
    }

    return containerProps;
  };

  const renderClickOutIcon = () => {
    if (disabled) {
      return null;
    }

    return (
      <OfferStoreIconWrapper>
        <IconOutgoingLink size={14} color="base400" />
      </OfferStoreIconWrapper>
    );
  };

  return (
    <>
      <div>
        <OfferLink
          container="a"
          containerProps={getContainerProps()}
          onClick={handleClick}
          aria-describedby={`offer-${uid}`}
        >
          <TitleSmallText containerProps={{ 'data-test': 'OfferName' }}>
            {highlightedName ||
              (name.length > 100 ? `${name.substring(0, 100)}...` : name)}
            <div>
              <BodySmallText color={FontColor.DARK} emphasis={FontEmphasis.LOW}>
                {store && store.name}
              </BodySmallText>
              {renderClickOutIcon()}
            </div>
          </TitleSmallText>
        </OfferLink>
      </div>

      {disabled && (
        <PopoverWrapper>
          <Popover
            id={`offer-${uid}`}
            visible={showPopover}
            width={250}
            offsetY={0}
            offsetX={0}
            verticalAlign={Alignment.Center}
            horizontalAlign={Alignment.Start}
            onClose={() => setShowPopover(false)}
            zIndex={zindex.popover}
          >
            <BodySmallText>
              {t('product', 'transparency.store-info')}
            </BodySmallText>
          </Popover>
        </PopoverWrapper>
      )}
    </>
  );
};

// Popover's outermost element must be absolute positioned to not claim space when in a css-grid layout
const PopoverWrapper = styled.div`
  position: absolute;
  z-index: ${zindex.popover};
`;
